import {
  CLYDE_PATH,
  ELEVINTIA_MARK_PATHS,
  discord,
} from "../discord/discord-theme";
import {
  chatScript,
  chatSeed,
  type ChatAuthor,
  type ChatEntry,
  type ReactionGlyph,
} from "./chat-script";

// Canvas space matches the phone screen aspect ratio (1.854 x 4.0 in the model).
export const CHAT_WIDTH = 720;
export const CHAT_HEIGHT = 1560;

const FONT =
  'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif';

const PAD_X = 28;
const AVATAR = 80;
const TEXT_X = PAD_X + AVATAR + 24;
const TEXT_W = CHAT_WIDTH - TEXT_X - PAD_X;
const HEADER_BOTTOM = 236;
const COMPOSER_TOP = CHAT_HEIGHT - 160;
const TYPING_ROW = 48;
const MESSAGES_BOTTOM = COMPOSER_TOP - TYPING_ROW - 6;

const COMMAND_LINE = 44;
const NAME_LINE = 48;
const BODY_SIZE = 32;
const BODY_LINE = 44;
const MESSAGE_GAP = 30;

const EMBED_BAR = 8;
const EMBED_PAD = 22;
const EMBED_INNER = TEXT_W - EMBED_BAR - EMBED_PAD * 2;
const BUTTON_H = 60;
const REACTION_H = 50;

const APPEAR_MS = 520;
const REACTION_DELAY_MS = 850;
const ROLL_MS = 220;
const MAX_MESSAGES = 14;

const clydePath = new Path2D(CLYDE_PATH);
const markPaths = ELEVINTIA_MARK_PATHS.map((d) => new Path2D(d));

type Word = { text: string; bold: boolean; token: boolean; space: boolean };
type PlacedWord = Word & { x: number };
type Line = PlacedWord[];

type EmbedLayout = {
  height: number;
  authorY?: number;
  title?: { y: number; lines: Line[] };
  description?: { y: number; lines: Line[] };
  fieldsY?: number;
  footerY?: number;
};

type LiveReaction = {
  count: number;
  target: number;
  startAt: number;
  nextAt: number;
  changedAt: number;
};

type LiveMessage = {
  entry: ChatEntry;
  lines: Line[];
  embed?: EmbedLayout;
  height: number;
  born: number;
  time: string;
  reaction?: LiveReaction;
};

type Phase =
  | { kind: "idle"; until: number }
  | { kind: "typing"; until: number; entry: ChatEntry }
  | { kind: "composing"; start: number; entry: ChatEntry }
  | { kind: "sending"; until: number; entry: ChatEntry };

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
const easeOutBack = (t: number) => {
  const c = 1.9;
  return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2);
};
const clamp01 = (t: number) => Math.min(1, Math.max(0, t));

function font(weight: number, size: number) {
  return `${weight} ${size}px ${FONT}`;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number | number[]
) {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}

/** Splits "**bold** text with @mentions" into styled words. */
function parseWords(text: string): Word[] {
  const words: Word[] = [];
  let spaceBefore = false;

  for (const part of text.split(/(\*\*[^*]+\*\*)/g)) {
    if (!part) continue;
    const bold =
      part.length > 4 && part.startsWith("**") && part.endsWith("**");
    const body = bold ? part.slice(2, -2) : part;

    for (const chunk of body.split(/(\s+)/)) {
      if (!chunk) continue;
      if (/^\s+$/.test(chunk)) {
        spaceBefore = true;
        continue;
      }
      const space = spaceBefore && words.length > 0;
      spaceBefore = false;

      // "@nova!" highlights "@nova" and leaves the punctuation plain.
      const token = chunk.match(/^([@#][\w-]+)(.*)$/);
      if (token) {
        words.push({ text: token[1]!, bold, token: true, space });
        if (token[2]) {
          words.push({ text: token[2], bold, token: false, space: false });
        }
      } else {
        words.push({ text: chunk, bold, token: false, space });
      }
    }
  }

  return words;
}

function layoutWords(
  ctx: CanvasRenderingContext2D,
  text: string,
  width: number,
  size: number,
  weight = 400
): Line[] {
  const lines: Line[] = [[]];
  let x = 0;

  for (const word of parseWords(text)) {
    ctx.font = font(word.bold ? 700 : weight, size);
    const w = ctx.measureText(word.text).width;
    ctx.font = font(weight, size);
    let gap = word.space ? ctx.measureText(" ").width : 0;

    if (x > 0 && x + gap + w > width) {
      lines.push([]);
      x = 0;
      gap = 0;
    }

    lines[lines.length - 1]!.push({ ...word, x: x + gap });
    x += gap + w;
  }

  return lines;
}

export class ChatRenderer {
  private messages: LiveMessage[] = [];
  private phase: Phase = { kind: "idle", until: 0 };
  private cursor = 0;
  private minute = 32;
  private sentCount = 0;
  private sendPulseAt = -Infinity;
  private lastActivity = 0;

  constructor(private ctx: CanvasRenderingContext2D) {}

  /** Fill the conversation instantly, for static renders and the first frame. */
  seed(now: number, extra = 0) {
    this.messages = [];
    this.cursor = 0;
    const settled = now - 10_000;
    for (const entry of chatSeed) this.push(entry, settled, true);
    for (let i = 0; i < extra; i++) {
      this.push(chatScript[this.cursor]!, settled, true);
      this.cursor = (this.cursor + 1) % chatScript.length;
    }
    this.phase = { kind: "idle", until: now + 900 };
  }

  private layoutEmbed(entry: ChatEntry): EmbedLayout | undefined {
    const embed = entry.embed;
    if (!embed) return undefined;
    const ctx = this.ctx;
    const layout: EmbedLayout = { height: 0 };
    let y = EMBED_PAD;

    if (embed.author) {
      layout.authorY = y;
      y += 40 + 10;
    }
    if (embed.title) {
      const lines = layoutWords(ctx, embed.title, EMBED_INNER, 29, 700);
      layout.title = { y, lines };
      y += lines.length * 40 + 8;
    }
    if (embed.description) {
      const lines = layoutWords(ctx, embed.description, EMBED_INNER, 27);
      layout.description = { y, lines };
      y += lines.length * 38 + 10;
    }
    if (embed.fields?.length) {
      layout.fieldsY = y;
      y += Math.ceil(embed.fields.length / 3) * 80;
    }
    if (embed.footer) {
      y += 6;
      layout.footerY = y;
      y += 32;
    }

    layout.height = y + EMBED_PAD - 6;
    return layout;
  }

  private push(entry: ChatEntry, born: number, settled = false) {
    const lines = entry.text
      ? layoutWords(this.ctx, entry.text, TEXT_W, BODY_SIZE)
      : [];
    const embed = this.layoutEmbed(entry);

    let height = NAME_LINE + lines.length * BODY_LINE;
    if (entry.command) height += COMMAND_LINE;
    if (embed) height += 10 + embed.height;
    if (entry.buttons) height += 14 + BUTTON_H;
    if (entry.reaction) height += 12 + REACTION_H;
    height = Math.max(height, AVATAR + 8) + MESSAGE_GAP;

    this.sentCount++;
    if (this.sentCount % 2 === 0) this.minute = (this.minute + 1) % 60;

    const reaction = entry.reaction && {
      count: settled ? entry.reaction.count : 0,
      target: entry.reaction.count,
      startAt: settled ? -Infinity : born + REACTION_DELAY_MS,
      nextAt: born + REACTION_DELAY_MS,
      changedAt: -Infinity,
    };

    this.messages.push({
      entry,
      lines,
      embed,
      height,
      born,
      time: `14:${String(this.minute).padStart(2, "0")}`,
      reaction,
    });

    if (this.messages.length > MAX_MESSAGES) this.messages.shift();
  }

  /** Advance the scripted conversation. Returns true while something is moving. */
  update(now: number) {
    const phase = this.phase;

    if (phase.kind === "idle" && now >= phase.until) {
      const entry = chatScript[this.cursor]!;
      this.cursor = (this.cursor + 1) % chatScript.length;
      const length = entry.text?.length ?? 40;
      this.phase = entry.composed
        ? { kind: "composing", start: now, entry }
        : {
            kind: "typing",
            until: now + 900 + Math.min(1200, length * 22),
            entry,
          };
    } else if (phase.kind === "typing" && now >= phase.until) {
      this.deliver(phase.entry, now);
    } else if (phase.kind === "composing") {
      const typed = Math.floor((now - phase.start) / 75);
      if (typed > (phase.entry.text?.length ?? 0) + 5) {
        this.sendPulseAt = now;
        this.phase = { kind: "sending", until: now + 180, entry: phase.entry };
      }
    } else if (phase.kind === "sending" && now >= phase.until) {
      this.deliver(phase.entry, now);
    }

    // Reactions trickle in one member at a time: 1, 2, 3...
    let reactionsMoving = false;
    for (const message of this.messages) {
      const reaction = message.reaction;
      if (!reaction) continue;
      if (reaction.count < reaction.target && now >= reaction.nextAt) {
        reaction.count++;
        reaction.changedAt = now;
        // Early reactions arrive quickly, later ones slow down.
        const progress = reaction.count / reaction.target;
        reaction.nextAt = now + 220 + progress * 500 + Math.random() * 450;
      }
      if (
        reaction.count < reaction.target ||
        now - reaction.changedAt < ROLL_MS + 120
      ) {
        reactionsMoving = true;
      }
    }

    return (
      reactionsMoving ||
      this.phase.kind !== "idle" ||
      now - this.lastActivity < APPEAR_MS + 1400 ||
      now - this.sendPulseAt < 500
    );
  }

  private deliver(entry: ChatEntry, now: number) {
    this.push(entry, now);
    this.lastActivity = now;
    const length = entry.text?.length ?? 60;
    this.phase = {
      kind: "idle",
      until: now + 1600 + Math.min(1800, length * 20),
    };
  }

  draw(now: number) {
    const ctx = this.ctx;
    ctx.fillStyle = discord.bg;
    ctx.fillRect(0, 0, CHAT_WIDTH, CHAT_HEIGHT);

    this.drawMessages(now);
    this.drawHeader();
    this.drawTyping(now);
    this.drawComposer(now);

    ctx.fillStyle = "rgba(255,255,255,0.6)";
    roundRect(ctx, CHAT_WIDTH / 2 - 110, CHAT_HEIGHT - 28, 220, 9, 5);
    ctx.fill();
  }

  // ---------- Chrome ----------

  private drawHeader() {
    const ctx = this.ctx;
    const W = CHAT_WIDTH;

    ctx.fillStyle = discord.bg;
    ctx.fillRect(0, 0, W, HEADER_BOTTOM);
    const fade = ctx.createLinearGradient(
      0,
      HEADER_BOTTOM,
      0,
      HEADER_BOTTOM + 50
    );
    fade.addColorStop(0, "rgba(30,31,34,1)");
    fade.addColorStop(1, "rgba(30,31,34,0)");
    ctx.fillStyle = fade;
    ctx.fillRect(0, HEADER_BOTTOM, W, 50);

    // Status bar
    ctx.fillStyle = "#FFFFFF";
    ctx.font = font(600, 30);
    ctx.textBaseline = "middle";
    ctx.fillText("9:41", 78, 66);
    for (let i = 0; i < 4; i++) {
      roundRect(ctx, W - 196 + i * 12, 74 - (i + 1) * 6, 8, (i + 1) * 6, 2);
      ctx.fill();
    }
    ctx.strokeStyle = "rgba(255,255,255,0.8)";
    ctx.lineWidth = 2.5;
    roundRect(ctx, W - 128, 54, 52, 26, 8);
    ctx.stroke();
    roundRect(ctx, W - 124, 58, 38, 18, 5);
    ctx.fill();

    // Channel header
    ctx.fillStyle = "#80848E";
    ctx.font = font(400, 66);
    ctx.fillText("#", PAD_X + 2, 172);
    ctx.fillStyle = discord.heading;
    ctx.font = font(700, 36);
    ctx.fillText("general", PAD_X + 62, 152);
    const nameWidth = ctx.measureText("general").width;
    ctx.strokeStyle = discord.icon;
    ctx.lineWidth = 3.5;
    ctx.beginPath();
    ctx.moveTo(PAD_X + 76 + nameWidth, 142);
    ctx.lineTo(PAD_X + 84 + nameWidth, 151);
    ctx.lineTo(PAD_X + 76 + nameWidth, 160);
    ctx.stroke();

    ctx.fillStyle = discord.green;
    ctx.beginPath();
    ctx.arc(PAD_X + 70, 196, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = discord.muted;
    ctx.font = font(500, 24);
    ctx.fillText("2,436 online", PAD_X + 86, 197);

    ctx.strokeStyle = discord.icon;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(W - PAD_X - 38, 164, 15, 0, Math.PI * 2);
    ctx.moveTo(W - PAD_X - 27, 175);
    ctx.lineTo(W - PAD_X - 14, 188);
    ctx.stroke();

    ctx.fillStyle = "rgba(255,255,255,0.06)";
    ctx.fillRect(0, HEADER_BOTTOM, W, 2);
  }

  private drawTyping(now: number) {
    if (this.phase.kind !== "typing") return;
    const ctx = this.ctx;
    const y = MESSAGES_BOTTOM + TYPING_ROW / 2 + 2;
    const x = PAD_X + 8;

    for (let i = 0; i < 3; i++) {
      const bounce = Math.max(0, Math.sin(now / 150 - i * 0.9));
      ctx.fillStyle = `rgba(219,222,225,${0.45 + bounce * 0.55})`;
      ctx.beginPath();
      ctx.arc(x + i * 17, y - bounce * 5, 6, 0, Math.PI * 2);
      ctx.fill();
    }

    const name = this.phase.entry.author.name;
    ctx.textBaseline = "middle";
    ctx.font = font(700, 23);
    ctx.fillStyle = discord.text;
    ctx.fillText(name, x + 56, y);
    const w = ctx.measureText(name).width;
    ctx.font = font(400, 23);
    ctx.fillStyle = discord.muted;
    ctx.fillText(" is typing…", x + 56 + w, y);
  }

  private drawComposer(now: number) {
    const ctx = this.ctx;
    const W = CHAT_WIDTH;
    const h = 76;
    const y = COMPOSER_TOP + 16;
    const cy = y + h / 2;

    // Attach button
    ctx.fillStyle = discord.surface;
    ctx.beginPath();
    ctx.arc(PAD_X + h / 2, cy, h / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = discord.icon;
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(PAD_X + h / 2 - 13, cy);
    ctx.lineTo(PAD_X + h / 2 + 13, cy);
    ctx.moveTo(PAD_X + h / 2, cy - 13);
    ctx.lineTo(PAD_X + h / 2, cy + 13);
    ctx.stroke();

    // Input
    const fieldX = PAD_X + h + 14;
    const fieldW = W - fieldX - PAD_X - h - 14;
    ctx.fillStyle = discord.surface;
    roundRect(ctx, fieldX, y, fieldW, h, h / 2);
    ctx.fill();

    let typed = "";
    if (this.phase.kind === "composing") {
      const count = Math.floor((now - this.phase.start) / 75);
      typed = (this.phase.entry.text ?? "").slice(0, count);
    } else if (this.phase.kind === "sending") {
      typed = this.phase.entry.text ?? "";
    }

    ctx.font = font(400, 28);
    ctx.textBaseline = "middle";
    if (typed) {
      ctx.fillStyle = discord.text;
      ctx.fillText(typed, fieldX + 28, cy + 1);
      if (Math.floor(now / 420) % 2 === 0) {
        ctx.fillRect(
          fieldX + 30 + ctx.measureText(typed).width,
          cy - 16,
          3,
          32
        );
      }
    } else {
      ctx.fillStyle = discord.faint;
      ctx.fillText("Message #general", fieldX + 28, cy + 1);
    }

    // Emoji button inside the field
    const ex = fieldX + fieldW - 40;
    ctx.strokeStyle = discord.icon;
    ctx.lineWidth = 3.5;
    ctx.beginPath();
    ctx.arc(ex, cy, 17, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(ex, cy + 2, 9, 0.15 * Math.PI, 0.85 * Math.PI);
    ctx.stroke();
    ctx.fillStyle = discord.icon;
    ctx.beginPath();
    ctx.arc(ex - 6, cy - 5, 2.6, 0, Math.PI * 2);
    ctx.arc(ex + 6, cy - 5, 2.6, 0, Math.PI * 2);
    ctx.fill();

    // Send button: Discord's paper plane on blurple.
    const pulse = clamp01((now - this.sendPulseAt) / 420);
    const scale = pulse < 1 ? 1 - Math.sin(pulse * Math.PI) * 0.12 : 1;
    ctx.save();
    ctx.translate(W - PAD_X - h / 2, cy);
    ctx.scale(scale, scale);
    ctx.fillStyle = discord.blurple;
    ctx.beginPath();
    ctx.arc(0, 0, h / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath();
    ctx.moveTo(-15, -14);
    ctx.lineTo(17, 0);
    ctx.lineTo(-15, 14);
    ctx.lineTo(-10, 2.5);
    ctx.lineTo(3, 0);
    ctx.lineTo(-10, -2.5);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
    ctx.lineCap = "butt";
  }

  // ---------- Messages ----------

  private drawMessages(now: number) {
    const ctx = this.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.rect(
      0,
      HEADER_BOTTOM,
      CHAT_WIDTH,
      MESSAGES_BOTTOM - HEADER_BOTTOM + TYPING_ROW
    );
    ctx.clip();

    let bottom = MESSAGES_BOTTOM;
    for (let i = this.messages.length - 1; i >= 0; i--) {
      const message = this.messages[i]!;
      const eased = easeOutCubic(clamp01((now - message.born) / APPEAR_MS));
      const top = bottom - message.height * eased;
      if (top + message.height < HEADER_BOTTOM - 40) break;

      ctx.save();
      ctx.globalAlpha = eased;
      ctx.translate(0, top + (1 - eased) * 36);
      this.drawMessage(message, now);
      ctx.restore();
      bottom = top;
    }

    ctx.restore();
  }

  private drawAvatar(author: ChatAuthor, x: number, y: number, size: number) {
    const ctx = this.ctx;
    const r = size / 2;
    ctx.save();
    ctx.fillStyle = author.avatar === "elevintia" ? "#0B1824" : author.avatar;
    ctx.beginPath();
    ctx.arc(x + r, y + r, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#FFFFFF";

    if (author.avatar === "elevintia") {
      const s = (size * 0.56) / 192;
      ctx.translate(x + r - 96 * s, y + r - 86 * s);
      ctx.scale(s, s);
      for (const path of markPaths) ctx.fill(path);
    } else {
      const s = (size * 0.6) / 24;
      ctx.translate(x + r - 12 * s, y + r - 12 * s);
      ctx.scale(s, s);
      ctx.fill(clydePath);
    }
    ctx.restore();
  }

  private drawWords(
    lines: Line[],
    x: number,
    y: number,
    size: number,
    lineHeight: number,
    color: string,
    weight = 400
  ) {
    const ctx = this.ctx;
    ctx.textBaseline = "middle";
    for (const line of lines) {
      for (const word of line) {
        ctx.font = font(word.bold ? 700 : weight, size);
        const wx = x + word.x;
        if (word.token) {
          const w = ctx.measureText(word.text).width;
          ctx.fillStyle = discord.mentionBg;
          roundRect(ctx, wx - 3, y - size * 0.62, w + 6, size * 1.24, 6);
          ctx.fill();
          ctx.fillStyle = discord.mentionText;
        } else {
          ctx.fillStyle = word.bold ? discord.heading : color;
        }
        ctx.fillText(word.text, wx, y);
      }
      y += lineHeight;
    }
  }

  private drawMessage(message: LiveMessage, now: number) {
    const ctx = this.ctx;
    const { entry } = message;
    const { author } = entry;
    let y = 0;

    if (entry.command) {
      this.drawCommandLine(entry.command.user, entry.command.name);
      y += COMMAND_LINE;
    }

    this.drawAvatar(author, PAD_X, y + 2, AVATAR);

    // Name, server tag, APP badge, time
    ctx.textBaseline = "middle";
    ctx.font = font(600, 31);
    ctx.fillStyle = author.color;
    ctx.fillText(author.name, TEXT_X, y + 20);
    let x = TEXT_X + ctx.measureText(author.name).width + 12;

    if (author.tag) {
      ctx.font = font(700, 19);
      const w = ctx.measureText("ELV").width + 44;
      ctx.fillStyle = "rgba(255,255,255,0.08)";
      roundRect(ctx, x, y + 6, w, 30, 7);
      ctx.fill();
      ctx.save();
      ctx.translate(x + 9, y + 12);
      ctx.scale(18 / 192, 18 / 192);
      ctx.fillStyle = "#8FD0FF";
      for (const path of markPaths) ctx.fill(path);
      ctx.restore();
      ctx.fillStyle = discord.text;
      ctx.fillText("ELV", x + 33, y + 22);
      x += w + 10;
    }

    if (author.app) {
      ctx.font = font(700, 18);
      const w = ctx.measureText("APP").width + 42;
      ctx.fillStyle = discord.blurple;
      roundRect(ctx, x, y + 7, w, 28, 6);
      ctx.fill();
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(x + 9, y + 21);
      ctx.lineTo(x + 14, y + 26);
      ctx.lineTo(x + 22, y + 16);
      ctx.stroke();
      ctx.fillStyle = "#FFFFFF";
      ctx.fillText("APP", x + 29, y + 22);
      x += w + 12;
    }

    ctx.font = font(400, 22);
    ctx.fillStyle = discord.muted;
    ctx.fillText(message.time, x, y + 22);
    y += NAME_LINE;

    if (message.lines.length) {
      this.drawWords(
        message.lines,
        TEXT_X,
        y + BODY_LINE / 2 - 2,
        BODY_SIZE,
        BODY_LINE,
        discord.text
      );
      y += message.lines.length * BODY_LINE;
    }

    if (entry.embed && message.embed) {
      y += 10;
      this.drawEmbed(entry, message.embed, y);
      y += message.embed.height;
    }

    if (entry.buttons) {
      y += 14;
      let bx = TEXT_X;
      for (const button of entry.buttons) {
        ctx.font = font(500, 26);
        const w = Math.max(120, ctx.measureText(button.label).width + 52);
        ctx.fillStyle =
          button.style === "primary"
            ? discord.blurple
            : button.style === "success"
              ? "#248046"
              : discord.secondaryButton;
        roundRect(ctx, bx, y, w, BUTTON_H, 10);
        ctx.fill();
        ctx.fillStyle = "#FFFFFF";
        ctx.textAlign = "center";
        ctx.fillText(button.label, bx + w / 2, y + BUTTON_H / 2 + 1);
        ctx.textAlign = "left";
        bx += w + 12;
      }
      y += BUTTON_H;
    }

    if (message.reaction && entry.reaction) {
      y += 12;
      this.drawReaction(entry.reaction.glyph, message.reaction, y, now);
    }
  }

  private drawCommandLine(user: ChatAuthor, command: string) {
    const ctx = this.ctx;
    const cy = COMMAND_LINE / 2 - 2;
    const lineX = PAD_X + AVATAR / 2;

    // The curved connector from the reply avatar up to the command line.
    ctx.strokeStyle = "#4E5058";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(lineX, COMMAND_LINE + 2);
    ctx.lineTo(lineX, cy + 14);
    ctx.arcTo(lineX, cy, lineX + 14, cy, 14);
    ctx.lineTo(TEXT_X - 10, cy);
    ctx.stroke();

    this.drawAvatar(user, TEXT_X, cy - 15, 30);
    ctx.textBaseline = "middle";
    ctx.font = font(500, 24);
    ctx.fillStyle = user.color;
    ctx.fillText(user.name, TEXT_X + 40, cy + 1);
    let x = TEXT_X + 40 + ctx.measureText(user.name).width;
    ctx.font = font(400, 24);
    ctx.fillStyle = discord.muted;
    ctx.fillText(" used ", x, cy + 1);
    x += ctx.measureText(" used ").width;
    ctx.fillStyle = discord.link;
    ctx.fillText(`/${command}`, x, cy + 1);
  }

  private drawEmbed(entry: ChatEntry, layout: EmbedLayout, top: number) {
    const ctx = this.ctx;
    const embed = entry.embed!;
    const x = TEXT_X + EMBED_BAR + EMBED_PAD;

    ctx.fillStyle = discord.surface;
    roundRect(ctx, TEXT_X, top, TEXT_W, layout.height, 8);
    ctx.fill();
    ctx.fillStyle = embed.color;
    roundRect(ctx, TEXT_X, top, EMBED_BAR, layout.height, [8, 0, 0, 8]);
    ctx.fill();

    ctx.textBaseline = "middle";

    if (embed.author && layout.authorY !== undefined) {
      this.drawAvatar(embed.author, x, top + layout.authorY + 4, 32);
      ctx.font = font(600, 25);
      ctx.fillStyle = discord.heading;
      ctx.fillText(embed.author.name, x + 44, top + layout.authorY + 21);
    }
    if (layout.title) {
      this.drawWords(
        layout.title.lines,
        x,
        top + layout.title.y + 20,
        29,
        40,
        discord.heading,
        700
      );
    }
    if (layout.description) {
      this.drawWords(
        layout.description.lines,
        x,
        top + layout.description.y + 19,
        27,
        38,
        discord.text
      );
    }
    if (embed.fields && layout.fieldsY !== undefined) {
      const col = EMBED_INNER / 3;
      const fieldsTop = top + layout.fieldsY;
      embed.fields.forEach((field, i) => {
        const fx = x + (i % 3) * col;
        const fy = fieldsTop + Math.floor(i / 3) * 80;
        ctx.font = font(700, 23);
        ctx.fillStyle = discord.heading;
        ctx.fillText(field.name, fx, fy + 16);
        ctx.font = font(400, 27);
        ctx.fillStyle = discord.text;
        ctx.fillText(field.value, fx, fy + 52);
      });
    }
    if (embed.footer && layout.footerY !== undefined) {
      ctx.font = font(500, 22);
      ctx.fillStyle = discord.muted;
      ctx.fillText(embed.footer, x, top + layout.footerY + 16);
    }
  }

  private drawReaction(
    glyph: ReactionGlyph,
    reaction: LiveReaction,
    y: number,
    now: number
  ) {
    if (reaction.count === 0) return;
    const ctx = this.ctx;
    const pop =
      reaction.startAt === -Infinity
        ? 1
        : clamp01((now - reaction.startAt) / 340);
    const scale = easeOutBack(pop);

    ctx.font = font(600, 25);
    const w = 80 + ctx.measureText(String(reaction.target)).width;
    const h = REACTION_H;

    ctx.save();
    ctx.translate(TEXT_X + w / 2, y + h / 2);
    ctx.scale(scale, scale);
    ctx.globalAlpha *= pop;
    ctx.translate(-w / 2, -h / 2);

    ctx.fillStyle = discord.surface;
    roundRect(ctx, 0, 0, w, h, 14);
    ctx.fill();
    this.drawGlyph(glyph, 32, h / 2);

    // The count rolls up like Discord's reaction counter.
    ctx.save();
    roundRect(ctx, 52, 4, w - 56, h - 8, 4);
    ctx.clip();
    ctx.fillStyle = discord.icon;
    ctx.textBaseline = "middle";
    const roll = clamp01((now - reaction.changedAt) / ROLL_MS);
    if (roll < 1 && reaction.count > 1) {
      const t = easeOutCubic(roll);
      ctx.fillText(String(reaction.count - 1), 58, h / 2 + 1 - t * 28);
      ctx.fillText(String(reaction.count), 58, h / 2 + 1 + (1 - t) * 28);
    } else {
      ctx.fillText(String(reaction.count), 58, h / 2 + 1);
    }
    ctx.restore();
    ctx.restore();
  }

  private drawGlyph(glyph: ReactionGlyph, cx: number, cy: number) {
    const ctx = this.ctx;
    ctx.save();
    ctx.translate(cx, cy);

    if (glyph === "heart") {
      ctx.fillStyle = "#DD2E44";
      ctx.beginPath();
      ctx.moveTo(0, 12);
      ctx.bezierCurveTo(-19, 0, -16, -16, -7, -15);
      ctx.bezierCurveTo(-3, -15, 0, -12, 0, -9);
      ctx.bezierCurveTo(0, -12, 3, -15, 7, -15);
      ctx.bezierCurveTo(16, -16, 19, 0, 0, 12);
      ctx.fill();
    } else if (glyph === "fire") {
      ctx.fillStyle = "#F4900C";
      ctx.beginPath();
      ctx.moveTo(0, -17);
      ctx.bezierCurveTo(11, -7, 15, 2, 12, 9);
      ctx.bezierCurveTo(9, 15, 4, 16, 0, 16);
      ctx.bezierCurveTo(-5, 16, -11, 14, -12, 7);
      ctx.bezierCurveTo(-13, 0, -7, -4, -4, -10);
      ctx.bezierCurveTo(-1, -5, 2, -5, 2, -8);
      ctx.bezierCurveTo(3, -11, 1, -15, 0, -17);
      ctx.fill();
      ctx.fillStyle = "#FFCC4D";
      ctx.beginPath();
      ctx.moveTo(0, -2);
      ctx.bezierCurveTo(6, 3, 7, 8, 5, 12);
      ctx.bezierCurveTo(3, 15, -3, 15, -5, 12);
      ctx.bezierCurveTo(-7, 8, -4, 4, 0, -2);
      ctx.fill();
    } else if (glyph === "tada") {
      // Party popper: cone plus confetti.
      ctx.fillStyle = "#DD2E44";
      ctx.beginPath();
      ctx.moveTo(-15, 16);
      ctx.lineTo(-6, -10);
      ctx.lineTo(10, 6);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#FFCC4D";
      ctx.beginPath();
      ctx.moveTo(-15, 16);
      ctx.lineTo(-9, -1);
      ctx.lineTo(1, 9);
      ctx.closePath();
      ctx.fill();
      const confetti: [number, number, string][] = [
        [6, -13, "#55ACEE"],
        [14, -6, "#77B255"],
        [12, -16, "#FFCC4D"],
        [2, -17, "#AA8DD8"],
        [16, 3, "#DD2E44"],
      ];
      for (const [x, y, color] of confetti) {
        ctx.fillStyle = color;
        ctx.fillRect(x - 2.5, y - 2.5, 5, 5);
      }
    } else {
      ctx.fillStyle = "#77B255";
      roundRect(ctx, -15, -15, 30, 30, 6);
      ctx.fill();
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 4.5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(-8, 0);
      ctx.lineTo(-2, 6);
      ctx.lineTo(8, -6);
      ctx.stroke();
    }

    ctx.restore();
  }
}
